export interface DayStripScrollPosition {
  left: number;
  width: number;
}

export interface DayStripScrollTarget {
  scrollLeft: number;
  shouldScroll: boolean;
}

/** Matches `DayCell` width in `DayStrip`. */
export const DAY_STRIP_CELL_WIDTH = 66;

/** Matches the day-strip flex `gap`. */
export const DAY_STRIP_GAP = 8;

/** Peek of the adjacent day kept visible after scrolling a cut-off day into view. */
export const DAY_STRIP_ADJACENT_DAY_SLIVER_PX = 24;

export function getDayStripScrollTarget({
  viewportWidth,
  currentScrollLeft,
  maxScrollLeft,
  selectedDay,
  previousDay,
  nextDay,
}: {
  viewportWidth: number;
  currentScrollLeft: number;
  maxScrollLeft: number;
  selectedDay: DayStripScrollPosition;
  previousDay?: DayStripScrollPosition;
  nextDay?: DayStripScrollPosition;
}): DayStripScrollTarget {
  const { left: dayLeft, width: dayWidth } = selectedDay;
  const dayRight = dayLeft + dayWidth;
  const viewportLeft = currentScrollLeft;
  const viewportRight = currentScrollLeft + viewportWidth;

  const isFullyVisible = dayLeft >= viewportLeft && dayRight <= viewportRight;

  if (isFullyVisible) {
    return { scrollLeft: currentScrollLeft, shouldScroll: false };
  }

  let targetScroll = currentScrollLeft;

  if (dayLeft < viewportLeft) {
    const minScrollForFullVisibilityWithLeftInset =
      dayLeft - DAY_STRIP_ADJACENT_DAY_SLIVER_PX;
    const minScrollForPreviousDaySliver = previousDay
      ? previousDay.left +
        previousDay.width +
        DAY_STRIP_GAP -
        DAY_STRIP_ADJACENT_DAY_SLIVER_PX
      : minScrollForFullVisibilityWithLeftInset;

    targetScroll = Math.min(
      targetScroll,
      Math.max(
        minScrollForFullVisibilityWithLeftInset,
        minScrollForPreviousDaySliver,
      ),
    );
  }

  if (dayRight > viewportRight) {
    const minScrollForFullVisibilityWithRightInset =
      dayRight - viewportWidth + DAY_STRIP_ADJACENT_DAY_SLIVER_PX;
    const minScrollForNextDaySliver = nextDay
      ? nextDay.left + DAY_STRIP_ADJACENT_DAY_SLIVER_PX - viewportWidth
      : minScrollForFullVisibilityWithRightInset;

    targetScroll = Math.max(
      targetScroll,
      minScrollForFullVisibilityWithRightInset,
      minScrollForNextDaySliver,
    );
  }

  const clampedScrollLeft = Math.max(0, Math.min(targetScroll, maxScrollLeft));

  return {
    scrollLeft: clampedScrollLeft,
    shouldScroll: Math.abs(clampedScrollLeft - currentScrollLeft) >= 1,
  };
}

export function scrollDayStripToSelectedDay({
  container,
  selectedDayElement,
  previousDayElement,
  nextDayElement,
}: {
  container: HTMLElement;
  selectedDayElement: HTMLElement;
  previousDayElement?: HTMLElement | null;
  nextDayElement?: HTMLElement | null;
}): boolean {
  const { scrollLeft: targetScrollLeft, shouldScroll } =
    getDayStripScrollTarget({
      viewportWidth: container.clientWidth,
      currentScrollLeft: container.scrollLeft,
      maxScrollLeft: container.scrollWidth - container.clientWidth,
      selectedDay: {
        left: selectedDayElement.offsetLeft,
        width: selectedDayElement.offsetWidth,
      },
      previousDay: previousDayElement
        ? {
            left: previousDayElement.offsetLeft,
            width: previousDayElement.offsetWidth,
          }
        : undefined,
      nextDay: nextDayElement
        ? {
            left: nextDayElement.offsetLeft,
            width: nextDayElement.offsetWidth,
          }
        : undefined,
    });

  if (!shouldScroll) {
    return false;
  }

  container.scrollTo({ left: targetScrollLeft, behavior: 'smooth' });
  return true;
}
